import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errorMessage } from "@/services/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthShell } from "@/components/AuthShell";
import { ModeToggle, PhoneField } from "@/components/PhoneField";
import { useLanguage } from "@/context/LanguageContext";

const field =
  "h-12 rounded-md border-[#22252F] bg-[#0F1116] text-white placeholder:text-[#4B5563] focus-visible:ring-[#2563EB]";
const labelCls = "text-xs uppercase tracking-[0.2em] text-[#94A3B8]";

export default function RegisterPage() {
  const { t } = useLanguage();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", invite_code: "", phone_dial: "+55", phone: "" });
  const [mode, setMode] = useState("email");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const payload = {
        name: form.name,
        password: form.password,
        invite_code: form.invite_code.trim().toUpperCase(),
        ...(mode === "phone" ? { phone_dial: form.phone_dial, phone: form.phone } : { email: form.email }),
      };
      await register(payload);
      navigate("/terminal", { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      overline={t("auth.registerOverline")}
      title={t("auth.registerTitle")}
      subtitle={t("auth.registerSubtitle")}
    >
      <form onSubmit={submit} className="space-y-5" data-testid="register-form">
        <ModeToggle mode={mode} onChange={(m) => { setMode(m); setError(""); }} testIdPrefix="register" />
        <div className="space-y-2">
          <Label htmlFor="name" className={labelCls}>{t("auth.name")}</Label>
          <Input id="name" required data-testid="register-name-input" value={form.name}
                 onChange={set("name")} placeholder={t("auth.namePlaceholder")} className={field} />
        </div>
        {mode === "phone" ? (
          <div className="space-y-2">
            <Label className={labelCls}>{t("auth.phone")}</Label>
            <PhoneField required dial={form.phone_dial} phone={form.phone}
                        onDial={(v) => setForm({ ...form, phone_dial: v })}
                        onPhone={(v) => setForm({ ...form, phone: v })}
                        testIdPrefix="register-phone" />
          </div>
        ) : (
        <div className="space-y-2">
          <Label htmlFor="email" className={labelCls}>{t("auth.email")}</Label>
          <Input id="email" type="email" required data-testid="register-email-input" value={form.email}
                 onChange={set("email")} placeholder="nome@dominio.com" className={field} />
        </div>
        )}
        <div className="space-y-2">
          <Label htmlFor="password" className={labelCls}>{t("auth.newPassword")}</Label>
          <Input id="password" type="password" required minLength={6} data-testid="register-password-input"
                 value={form.password} onChange={set("password")} placeholder={t("auth.passwordHint")} className={field} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="invite_code" className={labelCls}>{t("auth.inviteCode")}</Label>
          <Input id="invite_code" required data-testid="register-code-input" value={form.invite_code}
                 onChange={set("invite_code")} placeholder="EX: A1B2C3"
                 className={`${field} font-mono uppercase tracking-[0.2em]`} />
        </div>

        {error ? (
          <p data-testid="register-error"
             className="rounded-md border border-[#EF4444]/40 bg-[#EF4444]/10 px-4 py-3 text-sm text-[#FCA5A5]">
            {error}
          </p>
        ) : null}

        <Button type="submit" disabled={busy} data-testid="register-submit-button"
                className="group h-12 w-full rounded-md bg-[#2563EB] text-sm font-semibold uppercase tracking-[0.15em] text-white transition-colors hover:bg-[#1D4ED8]">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("auth.createAccount")}
          {!busy && <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />}
        </Button>

        <p className="text-sm text-[#94A3B8]">
          {t("auth.haveAccount")}{" "}
          <Link to="/login" data-testid="go-to-login-link"
                className="font-semibold text-white underline decoration-[#2563EB] decoration-2 underline-offset-4 transition-colors hover:text-[#60A5FA]">
            {t("auth.signIn")}
          </Link>
        </p>
      </form>
    </AuthShell>
  );
}
